import { BadgeCard, BadgeCardWidth, ContextMenu, ContextMenuItem } from '@bp/ui-components';
import classNames from 'classnames';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import React, { FC, useCallback, useMemo } from 'react';
import { useMutation } from 'urql';
import { useDebounceCallback } from 'usehooks-ts';
import { useMemorizedCacheTag } from '../../hooks/useMemorizedCacheTag';
import {
  CreateCardDocument,
  CreateSubjectContainerCardDocument,
  useDeleteCardsMutation,
} from '../../types/planung-graphql-client-defs';
import { createIntArray } from '../../utils/arrayFunc';
import { useTimetableStore } from '../TimetableGrid/TimetableProvider';
import { CardType } from '../TimetableVersion/graphql/types';
import styles from './PinboardCard.module.scss';

export interface PinboardCardProps {
  card: CardType;
  count?: number;
  passive?: boolean;
  onClick?: (event: React.MouseEvent<HTMLDivElement>, card: CardType) => void;
  className?: string | undefined;
}

export const PinboardCard: FC<PinboardCardProps> = observer(
  ({ card, count = 1, passive = false, onClick, className }) => {
    const context = useMemorizedCacheTag('LESSONS');

    const [, createSubjectContainerCard] = useMutation(CreateSubjectContainerCardDocument);
    const [, createCard] = useMutation(CreateCardDocument);
    const [, deleteCards] = useDeleteCardsMutation();

    const pinboardStore = useTimetableStore();

    const hasWarnings = card.warnings ? card.warnings.length > 0 : false;
    const hasAvailabilityWarnings = card.availabilityWarnings ? card.availabilityWarnings.length > 0 : false;
    const hasRooms = card.rooms.length !== 0;

    const convertToSingle = useCallback(async () => {
      let cardsToCreateCounter = 0;
      const cardsToDeleteUuids: string[] = [];

      if (card && card.duration && card.duration > 1) {
        cardsToDeleteUuids.push(card.uuid);
        cardsToCreateCounter = cardsToCreateCounter + card.duration;
      } else {
        return;
      }

      if (card.isSubjectContainer) {
        await Promise.all(
          createIntArray(cardsToCreateCounter).map(() => {
            return createSubjectContainerCard(
              {
                duration: 1,
                lessonUuid: card.lesson?.uuid,
                versionUuid: pinboardStore.getCurrentVersion()?.uuid,
                subjectContainerUuid: card.subject?.uuid,
              },
              context,
            );
          }),
        );
      } else {
        await Promise.all(
          createIntArray(cardsToCreateCounter).map(() => {
            return createCard(
              {
                duration: 1,
                lessonUuid: card.lesson?.uuid,
                versionUuid: pinboardStore.getCurrentVersion()?.uuid,
              },
              context,
            );
          }),
        );
      }

      await deleteCards({ where: { uuid_IN: cardsToDeleteUuids } }, context);
    }, [card, context, createCard, createSubjectContainerCard, deleteCards, pinboardStore]);

    const contextMenu: ContextMenuItem[] = useMemo(() => {
      const menu: ContextMenuItem[] = [];

      menu.push({
        type: 'component',
        node: <div className={styles['menu-name']}>{card.subject?.name}</div>,
      });

      if (hasWarnings || hasAvailabilityWarnings || !hasRooms) {
        menu.push({
          type: 'ruler',
        });
      }

      if (hasWarnings) {
        menu.push({
          type: 'component',
          node: <></>,
        });
      }

      if (hasAvailabilityWarnings) {
        menu.push({
          type: 'component',
          node: <></>,
        });
      }

      if (!hasRooms) {
        menu.push({
          type: 'component',
          node: <div className={styles['menu-info']}>{t('pinboard.reason.noRoom')}</div>,
        });
      }

      menu.push(
        {
          type: 'ruler',
        },
        // {
        //   label: pinboardStore.pickedCard?.card === card ? t('pinboard.actions.unpick') : t('pinboard.actions.pick'),
        //   onClick: (e) => {
        //     e.stopPropagation();
        //     if (card && !card.locked) {
        //       if (pinboardStore.pickedCard?.card === card) {
        //         pinboardStore.pickPlacedCard(null);
        //       } else {
        //         pinboardStore.pickPlacedCard(card, { x: e.clientX, y: e.clientY });
        //       }
        //     }
        //   },
        //   disabled: card.locked,
        // },
        {
          label: pinboardStore.pinnedCard === card.uuid ? t('pinboard.actions.unpin') : t('pinboard.actions.pin'),
          onClick: (e) => {
            e.stopPropagation();
            if (card) {
              if (pinboardStore.pinnedCard === card.uuid) {
                pinboardStore.setPinnedCard(null);
              } else {
                pinboardStore.setPinnedCard(card.uuid);
              }
            }
          },
        },
        {
          label: pinboardStore.lockedCards.has(card.uuid) ? t('pinboard.actions.unlock') : t('pinboard.actions.lock'),
          onClick: () => {
            if (card) {
              if (pinboardStore.lockedCards.has(card.uuid)) pinboardStore.unlockCard(card.uuid);
              else pinboardStore.lockCard(card.uuid);
            }
          },
        },
        {
          type: 'ruler',
        },
        {
          label: t('rooms.title', { count: 2 }),
          tooltip: t('pinboard.actions.noRoomsHint'),
          disabled: true,
        },
        {
          label: t('pinboard.actions.singleHour'),
          onClick: async () => {
            await convertToSingle();
          },
          disabled: !!card.duration && card.duration <= 1,
        },
        {
          type: 'ruler',
        },
        {
          label: t('common.remove'),
          onClick: () => {
            if (card) pinboardStore.discardPickedCard();
          },
          type: 'error',
          disabled: !card.startTimeGridEntry,
        },
      );

      return menu;
    }, [card, hasWarnings, hasAvailabilityWarnings, hasRooms, pinboardStore, convertToSingle]);

    const wrapperClasses = classNames(
      styles['pinboard-card'],
      {
        [styles['is-multi']]: count >= 1,
        [styles['is-picked']]: pinboardStore.pickedCard?.cardUuid === card.uuid,
        [styles['is-pinned']]: pinboardStore.pinnedCard === card.uuid,
        [styles['is-locked']]: pinboardStore.lockedCards.has(card.uuid),
      },
      className,
    );

    const menuClasses = classNames(styles.menu, {
      [styles['is-placed']]: !!card.startTimeGridEntry,
    });

    const debounceOnCardHover = useDebounceCallback(() => {
      pinboardStore.setHoveredCard(card.uuid);
    }, 500);

    const onMouseEnter = () => {
      if (!pinboardStore.hoveredCard) {
        pinboardStore.setHoveredCard(card.uuid);
      } else {
        debounceOnCardHover();
      }
    };
    const onMouseLeave = () => {
      debounceOnCardHover.cancel();
    };

    return (
      <ContextMenu className={menuClasses} disabled={passive} data={contextMenu} usePortal={false}>
        <div
          className={wrapperClasses}
          title={card.subject?.name ?? ''}
          onClick={(e) => {
            onClick && onClick(e, card);
          }}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          <BadgeCard
            label={card.subject?.shortName ?? ''}
            width={card.duration as BadgeCardWidth}
            rows={card.badgeCardRows}
            color={card.badgeCardTextColor}
            count={count}
          />
        </div>
      </ContextMenu>
    );
  },
);
